import * as React from "react";

import { cn } from "./utils";

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom type-body", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        // The header reads as a header from three cues that hold in both themes:
        // a foreground-overlay tint (darkens on light, lightens on dark; surface-3
        // is plain #FFF in light, which made the header *lighter* than the gray
        // card and barely legible as a header), a medium-weight label (on
        // TableHead), and a solid bottom divider. Body rows are flat — no zebra.
        "bg-foreground/[0.05] [&_tr]:border-b [&_tr]:border-border",
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-surface-3 border-t border-border [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "data-[state=selected]:bg-muted border-b border-border transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // The redesign table standard (docs/design/prototypes/redesign/SYSTEM.md §4): a table
        // is line-less — rows separate on horizontal borders ONLY, no vertical
        // column dividers — and every cell pads a flat px-4. The 16px cell inset
        // is load-bearing: a Ledger "hangs" its card into the gutter by exactly
        // `1px border + 1rem padding = 17px` so first-column content lands on
        // grid line 1; a different padding breaks that alignment everywhere.
        // Vertical sizing mirrors TableCell's py-2 (not a fixed h-10) so the
        // header row is exactly as tall as a body row. font-medium gives the
        // label presence without all-caps (the muted, non-caps header rule).
        "text-muted-foreground font-medium px-[var(--content-gutter)] py-[var(--table-py)] text-left align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        // Flat px-4 cells, no vertical dividers — the line-less standard; the
        // 16px inset is the Ledger hang's alignment basis (see TableHead above).
        "px-[var(--content-gutter)] py-[var(--table-py)] align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 type-body", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
